import { useRef } from 'react';
import { AlertTriangle, Check, Download, RotateCcw, Upload } from 'lucide-react';
import { BlocoCampos, CampoArea, CampoTexto, Grade, Interruptor } from '../componentes/campos';
import { STATUS_MANUAL, formatarMomento, type StatusManual } from '../manual-api';
import { SECOES } from '../manual-defaults';
import type { DocumentoMontado } from '../documento/montar-documento';
import type { ManualOpcoes, SecaoId } from '../manual-types';
import type { PropsSecao } from './tipos';

const OPCOES_COMPOSICAO: { chave: keyof ManualOpcoes; rotulo: string; descricao: string }[] = [
  { chave: 'incluirSumario', rotulo: 'Sumário automático', descricao: 'Gera o índice com os números de página recalculados a cada edição.' },
  { chave: 'numerarPaginas', rotulo: 'Numeração no rodapé', descricao: 'Exibe “Página n de N” ao lado do endereço institucional.' },
  { chave: 'incluirSimbologia', rotulo: 'Simbologia (1.3)', descricao: 'Publica os quatro blocos de alerta usados no manual.' },
  { chave: 'incluirNormas', rotulo: 'Legislação e normas (1.5)', descricao: 'Inclui a tabela de normas aplicáveis.' },
  { chave: 'incluirEspecEletricas', rotulo: 'Dados elétricos (3.x)', descricao: 'Tabela de especificações elétricas.' },
  { chave: 'incluirEspecHidraulicas', rotulo: 'Dados hidráulicos (3.x)', descricao: 'Tabela de especificações hidráulicas.' },
  { chave: 'incluirEspecPneumaticas', rotulo: 'Dados pneumáticos (3.x)', descricao: 'Tabela de especificações pneumáticas.' },
  { chave: 'incluirAssistencia', rotulo: 'Assistência técnica (4.6)', descricao: 'Item final do capítulo de instalação.' },
  { chave: 'incluirUsoImprevisivel', rotulo: 'Mau uso razoavelmente previsível (5.6)', descricao: 'Relação de maus usos previsíveis, expressamente proibidos.' },
  { chave: 'incluirLubrificacao', rotulo: 'Plano de lubrificação (6.x)', descricao: 'Tabela de pontos, produtos e periodicidade.' },
  { chave: 'incluirGlossario', rotulo: 'Glossário (8.1)', descricao: 'Definições dos termos técnicos.' },
  { chave: 'incluirAbreviaturas', rotulo: 'Abreviaturas (8.2)', descricao: 'Siglas utilizadas no documento.' },
];

export function SecaoDocumento({ editor, documento, aoIrParaSecao }: PropsSecao & {
  documento: DocumentoMontado;
  aoIrParaSecao: (secao: SecaoId) => void;
}) {
  const { dados, manual, avaliacao, definirCampo, definirOpcao, definirStatus, exportar, importar, limpar } = editor;
  const entradaArquivo = useRef<HTMLInputElement>(null);

  const secoesComPendencia = SECOES
    .map((secao) => ({ secao, avaliacao: avaliacao.secoes[secao.id] }))
    .filter((item) => item.avaliacao && item.avaliacao.pendencias.length > 0);

  return (
    <>
      {manual && (
        <BlocoCampos
          titulo="Registro no histórico"
          descricao="Este manual está gravado no projeto. Cada versão registrada guarda um retrato completo do documento, com data, autor e resumo."
        >
          <div className="manual-registro-grade">
            <div><span>Código</span><strong>{manual.codigo}</strong></div>
            <div><span>Revisão</span><strong>Rev. {String(manual.revisao).padStart(2, '0')}</strong></div>
            <div><span>Versões registradas</span><strong>{manual._count?.versoes ?? 0}</strong></div>
            <div><span>Última alteração</span><strong>{formatarMomento(manual.updatedAt)}</strong></div>
          </div>
          <label className="manual-field is-wide">
            Status do manual
            <select value={manual.status} onChange={(evento) => void definirStatus(evento.target.value as StatusManual)}>
              {STATUS_MANUAL.map((item) => (
                <option key={item.valor} value={item.valor}>{item.rotulo} — {item.descricao}</option>
              ))}
            </select>
            <small>Marcar como concluído ou liberado registra automaticamente uma versão do documento.</small>
          </label>
          <p className="manual-dica">
            Use <strong>Versões</strong>, no topo da tela, para registrar um ponto de retorno, comparar o que mudou
            entre duas versões ou restaurar um texto anterior — restaurar nunca apaga o trabalho atual, que é
            guardado antes.
          </p>
        </BlocoCampos>
      )}

      <BlocoCampos titulo="Composição do documento" descricao="Cada chave liga ou desliga um bloco do manual. A paginação e o sumário se ajustam sozinhos.">
        <div className="manual-opcoes-grade">
          {OPCOES_COMPOSICAO.map((opcao) => (
            <Interruptor
              key={String(opcao.chave)}
              rotulo={opcao.rotulo}
              descricao={opcao.descricao}
              ativo={Boolean(dados.opcoes[opcao.chave])}
              aoMudar={(ativo) => definirOpcao(opcao.chave, ativo as ManualOpcoes[typeof opcao.chave])}
            />
          ))}
        </div>
      </BlocoCampos>

      <BlocoCampos titulo="Identidade e rodapé" descricao="Textos institucionais repetidos em todas as páginas do miolo.">
        <Grade>
          <CampoTexto largo rotulo="Título do documento" valor={dados.opcoes.tituloDocumento} aoMudar={(valor) => definirOpcao('tituloDocumento', valor)} />
          <CampoTexto rotulo="Reconstituidora" valor={dados.opcoes.reconstituidora} aoMudar={(valor) => definirOpcao('reconstituidora', valor)} />
          <CampoTexto rotulo="Site no rodapé" valor={dados.opcoes.site} aoMudar={(valor) => definirOpcao('site', valor)} />
        </Grade>
        <CampoArea rotulo="Aviso legal do rodapé" linhas={3} valor={dados.opcoes.avisoLegal} aoMudar={(valor) => definirOpcao('avisoLegal', valor)} />
        <label className="manual-field is-wide">
          Densidade do texto — {dados.opcoes.densidadeTexto}%
          <input
            type="range"
            min={90}
            max={115}
            step={1}
            value={dados.opcoes.densidadeTexto}
            onChange={(evento) => definirOpcao('densidadeTexto', Number(evento.target.value))}
          />
          <small>Ajusta o corpo do texto do miolo. A paginação é medida na própria folha e se refaz sozinha.</small>
        </label>
      </BlocoCampos>

      <BlocoCampos titulo="Revisão antes da emissão" descricao={`Documento com ${documento.totalPaginas + 1} páginas e ${documento.sumario.filter((entrada) => entrada.nivel === 1).length} capítulos.`}>
        <div className="manual-revisao-resumo">
          <div>
            <strong>{avaliacao.geral}%</strong>
            <span>preenchido</span>
          </div>
          <div className={avaliacao.pendenciasCriticas ? 'is-alerta' : 'is-ok'}>
            <strong>{avaliacao.pendenciasCriticas}</strong>
            <span>pendências críticas</span>
          </div>
          <div>
            <strong>{documento.totalPaginas + 1}</strong>
            <span>páginas geradas</span>
          </div>
        </div>

        {secoesComPendencia.length === 0 ? (
          <p className="manual-revisao-ok"><Check />Todos os itens verificados foram preenchidos.</p>
        ) : (
          <div className="manual-revisao-lista">
            {secoesComPendencia.map(({ secao, avaliacao: item }) => (
              <div key={secao.id} className="manual-revisao-item">
                <header>
                  <strong>{secao.id} · {secao.titulo}</strong>
                  <button type="button" className="manual-ghost-button" onClick={() => aoIrParaSecao(secao.id)}>Abrir etapa</button>
                </header>
                <ul>
                  {item.pendencias.map((pendencia) => (
                    <li key={pendencia.rotulo} className={pendencia.critico ? 'is-critico' : ''}>
                      {pendencia.critico && <AlertTriangle />}
                      {pendencia.rotulo}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </BlocoCampos>

      <BlocoCampos
        titulo="Arquivo do manual"
        descricao={manual
          ? 'O texto é salvo sozinho no servidor a cada edição. Exporte o JSON para levar o conteúdo a outro projeto ou guardar fora do sistema.'
          : 'Este manual ainda é um rascunho deste navegador. Use “Salvar no histórico”, no topo da tela, para versioná-lo junto com os demais manuais do projeto.'}
      >
        <div className="manual-arquivo-acoes">
          <button type="button" className="manual-ghost-button" onClick={exportar}>
            <Download />Exportar JSON
          </button>
          <button type="button" className="manual-ghost-button" onClick={() => entradaArquivo.current?.click()}>
            <Upload />Importar JSON
          </button>
          <button
            type="button"
            className="manual-ghost-button is-danger"
            onClick={() => {
              const aviso = manual
                ? 'Limpar todo o conteúdo deste manual? O documento gravado será substituído por um em branco — o histórico de versões continua disponível para restaurar.'
                : 'Limpar todo o conteúdo deste manual? O rascunho salvo será apagado.';
              if (window.confirm(aviso)) limpar();
            }}
          >
            <RotateCcw />Limpar manual
          </button>
          <input
            ref={entradaArquivo}
            type="file"
            accept="application/json"
            hidden
            onChange={async (evento) => {
              const arquivo = evento.target.files?.[0];
              if (!arquivo) return;
              try {
                await importar(arquivo);
              } catch {
                window.alert('Não foi possível ler o arquivo. Verifique se ele foi exportado por esta tela.');
              } finally {
                evento.target.value = '';
              }
            }}
          />
        </div>
        <p className="manual-dica">
          Campos como transporte, armazenagem, içamento e descarte publicam o texto institucional padrão quando deixados em branco —
          o conteúdo específico da máquina nunca é inventado pelo gerador.
        </p>
        <CampoTexto
          largo
          rotulo="Código de rastreabilidade"
          valor={dados.codigoRastreabilidade}
          placeholder="Repetido na capa e no capítulo 1"
          aoMudar={(valor) => definirCampo('codigoRastreabilidade', valor)}
        />
      </BlocoCampos>
    </>
  );
}
